import { getPreferredCurrency, setPreferredCurrency } from './currency';

/**
 * Static FX table for *display only* on the marketing pricing page.
 * Pricing is contracted in USD; this conversion just shows the user the
 * approximate equivalent in their local currency on first paint. It is
 * refreshed manually with each pricing review.
 *
 * Numbers are illustrative as of 2026-05.
 */
const USD_TO: Record<string, number> = {
  USD: 1.0,
  JPY: 152,
  EUR: 0.93,
  GBP: 0.80,
  CAD: 1.36,
  AUD: 1.51,
  KRW: 1352,
  CNY: 7.20,
  HKD: 7.82,
  SGD: 1.35,
  TWD: 32.4,
  INR: 83.2,
  BRL: 5.0,
  MXN: 17.1,
};

export function supportedDisplayCurrencies(): string[] {
  return Object.keys(USD_TO);
}

export function currentDisplayCurrency(): string {
  const pref = getPreferredCurrency();
  if (pref && USD_TO[pref] !== undefined) return pref;
  return 'USD';
}

export function setDisplayCurrency(code: string): void {
  setPreferredCurrency(code);
}

/**
 * Convert a USD amount to the user's display currency. Round to "nice"
 * numbers per currency (yen has no fractional units, etc.).
 */
export function fromUsd(amount: number, currency: string = currentDisplayCurrency()): { amount: number; currency: string } {
  const rate = USD_TO[currency] ?? 1;
  const raw = amount * rate;
  const rounded = roundForCurrency(raw, currency);
  return { amount: rounded, currency };
}

export function formatDisplayCurrency(amount: number, currency: string = currentDisplayCurrency(), locale = 'en-US'): string {
  const decimals = currency === 'JPY' || currency === 'KRW' || currency === 'TWD' ? 0 : 0;
  return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: decimals }).format(amount);
}

function roundForCurrency(n: number, currency: string): number {
  if (currency === 'JPY' || currency === 'KRW') {
    if (n >= 1000) return Math.round(n / 100) * 100;
    return Math.round(n);
  }
  if (n >= 50) return Math.round(n);
  return Math.round(n * 100) / 100;
}
